import { Search } from '@mui/icons-material';
import { InputAdornment, Stack, TextField, Typography } from '@mui/material';
import PrimaryBtn from 'components/shared/Buttons/PrimaryButton';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedValue, useRefundProductsSWR } from '../../../hooks';
import useRefundLineItems from '../../../hooks/useRefundLineItems';
import RefundLineItem from '../../../models/RefundLineItem';
import RefundProductDto from '../../../models/RefundProductDto';
import ProductsTable from '../SearchProductsTable';

interface AddProductsDialogContentProps {
    onSubmit: (items: RefundLineItem[]) => void;
}

const AddProductsDialogContent: React.FC<AddProductsDialogContentProps> = (
    props
) => {
    const { t } = useTranslation('refund', {
        keyPrefix: 'PRODUCTS_STEP.ADD_PRODUCT_MODAL',
    });

    const { onSubmit } = props;

    const { add, remove, cache } = useRefundLineItems();

    const { debouncedValue, value, onValueChange } =
        useDebouncedValue<string>('');

    const [page, setPage] = useState<number>(0);

    const [rowsPerPage, setRowsPerPage] = useState<number>(10);

    const handleChangePage = (
        _: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ): void => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ): void => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const { refundProducts, totalCount, loading } = useRefundProductsSWR({
        size: rowsPerPage,
        sku: debouncedValue,
        page,
    });

    const handleSelectChange = (
        value: boolean,
        refundProduct: RefundProductDto
    ): void => {
        if (value) return add(refundProduct);
        remove(refundProduct.itemNo);
    };

    const handleSubmit = (): void => {
        onSubmit(Array.from(cache.values()));
    };

    return (
        <Stack gap={2} paddingTop={2} padding={2}>
            <TextField
                value={value}
                onChange={(event) => onValueChange(event.target.value)}
                sx={{ width: '100%' }}
                placeholder={t('SEARCH')}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <Search />
                        </InputAdornment>
                    ),
                }}
            />

            <Typography
                variant="caption"
                marginBottom={2}
                textAlign="left"
                width="100%"
                color="CaptionText"
            >
                {t('DESCRIPTION')}
            </Typography>

            <ProductsTable
                items={refundProducts}
                cache={cache}
                page={page}
                rowsPerPage={rowsPerPage}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                onSelectChange={handleSelectChange}
                totalCount={totalCount}
                loading={loading}
            />

            <PrimaryBtn disabled={loading} onClick={handleSubmit}>
                {t('SUBMIT')}
            </PrimaryBtn>
        </Stack>
    );
};

export default AddProductsDialogContent;
